import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./Courses.css";

// Local image imports - ensure these paths and filenames actually exist in your project
import Java from '../../../Images/Java.svg';
import Webdev from '../../../Images/webdev.png';
import Cyber from '../../../Images/cyber.avif';
import Python from '../../../Images/python.jpeg';
import AWS from '../../../Images/AWS.svg';
import Salesforce from '../../../Images/salesforce.png';
import Odoo from '../../../Images/odoo.png';
import MERN from '../../../Images/Mearn.svg';
import C from '../../../Images/C.png';
import Cpp from '../../../Images/C++.svg'; // renamed from C++.svg to Cplusplus.svg (avoid plus in filename/var)
import Embedded from '../../../Images/embedded.png';
import Computer from '../../../Images/computern.png'; // fixed likely typo 'computern:w.png' -> 'computer.png'
import OS from '../../../Images/os.png';
import DA from '../../../Images/dataanlitics.png';
import DS from '../../../Images/datascience.png';
import AIML from '../../../Images/AI&ML.png';
import Zoho from '../../../Images/ZOHO.png';
import IOT from '../../../Images/IOT.png';
import dot from '../../../Images/dotnet.png';
import DM from '../../../Images/digital.png';

const courses = [
  {
    id: "java",
    title: "Java Development",
    desc:
      "Master Java programming and learn to build robust backend systems, enterprise applications, and Android apps.",
    accent: "java",
    image: Java,
  },
  {
    id: "webdev",
    title: "Web Development",
    desc:
      "Learn front-end and back-end web technologies to become a full-stack web developer.",
    accent: "webdev",
    image: Webdev,
  },
  {
    id: "cyber",
    title: "Cyber Security",
    desc:
      "Gain hands-on experience in protecting systems, networks, and programs from cyber attacks.",
    accent: "cyber",
    image: Cyber,
  },
  {
    id: "python",
    title: "Python Developer",
    desc:
      "Learn Python programming for web development, automation, and data analysis.",
    accent: "python",
    image: Python,
  },
  {
    id: "aws",
    title: "AWS Cloud Computing",
    desc:
      "Build cloud-based applications and infrastructure using Amazon Web Services.",
    accent: "aws",
    image: AWS,
  },
  {
    id: "salesforce",
    title: "Salesforce Developer",
    desc:
      "Learn Salesforce development, CRM customization, and business process automation.",
    accent: "salesforce",
    image: Salesforce,
  },
  {
    id: "odoo",
    title: "Odoo Developer",
    desc:
      "Develop business applications using Odoo’s ERP framework and Python-based modules.",
    accent: "odoo",
    image: Odoo,
  },
  {
    id: "mern",
    title: "MERN Stack Development",
    desc:
      "Master MongoDB, Express, React, and Node.js to build modern full-stack web apps.",
    accent: "mern",
    image: MERN,
  },
  {
    id: "c",
    title: "C Programming",
    desc:
      "Learn the fundamentals of programming with C, the foundation of many modern languages.",
    accent: "c",
    image: C,
  },
  {
    id: "cpp",
    title: "C++ Programming",
    desc:
      "Master object-oriented programming and high-performance application development with C++.",
    accent: "cpp",
    image: Cpp,
  },
  {
    id: "embedded",
    title: "Embedded Systems",
    desc:
      "Learn to design, program, and develop embedded systems for IoT, robotics, and automation.",
    accent: "embedded",
    image: Embedded,
  },
  {
    id: "networking",
    title: "Computer Networking",
    desc:
      "Understand the fundamentals of computer networks, protocols, and network security.",
    accent: "networking",
    image: Computer,
  },
  {
    id: "os",
    title: "Operating Systems",
    desc:
      "Learn how operating systems work, manage hardware resources, and provide services for computer programs.",
    accent: "os",
    image: OS,
  },
  {
    id: "dataanalytics",
    title: "Data Analytics",
    desc:
      "Analyze and visualize data to make informed business decisions using modern tools.",
    accent: "dataanalytics",
    image: DA,
  },
  {
    id: "datascience",
    title: "Data Science",
    desc:
      "Learn data manipulation, visualization, and machine learning with Python and AI tools.",
    accent: "datascience",
    image: DS,
  },
  {
    id: "aiml",
    title: "AI & Machine Learning",
    desc:
      "Understand artificial intelligence and machine learning concepts to create predictive models and intelligent systems.",
    accent: "aiml",
    image: AIML,
  },
  {
    id: "zoho",
    title: "Zoho Developer",
    desc:
      "Learn to build and customize business applications using Zoho CRM, Creator, and Deluge scripting.",
    accent: "zoho",
    image: Zoho,
  },
  {
    id: "iot",
    title: "Internet of Things (IoT)",
    desc:
      "Learn IoT concepts, sensors, microcontrollers, and build smart connected systems.",
    accent: "iot",
    image: IOT,
  },
  {
    id: "dotnet",
    title: ".NET Development",
    desc:
      "Learn .NET framework, C#, and build scalable web, desktop, and enterprise applications.",
    accent: "dotnet",
    image: dot,
  },
  {
    id: "digitalmarketing",
    title: "Digital Marketing",
    desc:
      "Master SEO, social media marketing, Google Ads, and analytics to grow businesses online.",
    accent: "digitalmarketing",
    image: DM,
  }
];

function Card({ item }) {
  // If image is an object with a default property (some bundlers), fall back gracefully:
  const src = typeof item.image === "object" && item.image?.default ? item.image.default : item.image;

  return (
    <motion.article whileHover={{ y: -4 }} className={`card ${item.accent}`} aria-labelledby={`${item.id}-title`}>
      <div className="card-image-wrapper" role="img" aria-label={`${item.title} image`}>
        <img
          src={src}
          alt={item.title}
          className="card-image"
          loading="lazy"
          decoding="async"
          // you can add srcSet here for responsive images if available:
          // srcSet={`${src} 1x, ${src2x} 2x`}
        />
      </div>

      <h3 id={`${item.id}-title`} className="card-title">{item.title}</h3>
      <p className="card-desc">{item.desc}</p>
      <Link to="/contact" className="card-btn-link" aria-label={`Start learning ${item.title}`}>
        <button className="card-btn">Start Learning!</button>
      </Link>
    </motion.article>
  );
}

export default function CoursesPage() {
  return (
    <main className="courses-container">
      <header className="courses-header">
        <h1>Our Courses</h1>
        <p className="courses-subtitle">
          Explore our top professional training programs and boost your career in technology.
        </p>
      </header>

      <section className="courses-grid" aria-live="polite">
        {courses.map((c) => (
          <Card key={c.id} item={c} />
        ))}
      </section>
    </main>
  );
}
